/* ═══════════════════════════════════════════════════════════════════════
   NoteFlow — stmt-equity.ts
   buildEquityStatement()
   ═══════════════════════════════════════════════════════════════════════ */

import { state, type LineItem } from './state';
import {
  prepareStatementData,
  restoreStatementData,
} from './consolidation';
import {
  sum,
  rows,
  hasPriorData,
  computeNetIncome,
  isTrialBalanceClosed,
  tr,
  sectionHeader,
  columnHeaders,
  stmtHeader,
  el,
} from './utils';

// ─── Types ─────────────────────────────────────────────────────────────────

interface EquityChange {
  label: string;
  amount: number;
}

// ─── Statement of Stockholders' Equity ─────────────────────────────────────

/**
 * Builds the statement of stockholders' equity showing changes
 * in equity components.
 */
export function buildEquityStatement(): void {
  prepareStatementData();
  try {
  const multi: boolean = hasPriorData();
  const netIncome: number = computeNetIncome(state.currentData);
  const isClosed: boolean = isTrialBalanceClosed(state.currentData);
  const pIsClosed: boolean = multi ? isTrialBalanceClosed(state.priorData) : false;

  // For unclosed TB: ending equity = sum(equity) + NI (NI flows in)
  // For closed TB: ending equity = sum(equity) (NI already in RE)
  const totalEquity: number = sum(state.currentData, 'equity') + (isClosed ? 0 : netIncome);
  const pTotalEquity: number = multi ? sum(state.priorData, 'equity') : 0;
  const pNetIncome: number = multi ? computeNetIncome(state.priorData) : 0;
  const pTotalEquityWithNI: number = pTotalEquity + (pIsClosed ? 0 : pNetIncome);

  // Detect dividends from RE change (closed TBs reflect them in RE)
  function getRE(d: any): number {
    return rows(d, 'equity')
      .filter((i: LineItem) => /retained\s*earnings|accumulated\s*(deficit|surplus)/i.test(i.label))
      .reduce((acc: number, i: LineItem) => acc + (parseFloat(String(i.amount)) || 0), 0);
  }
  let dividends: number = 0;
  if (multi) {
    const reCur = getRE(state.currentData);
    const rePrior = getRE(state.priorData);
    const reChange = reCur - rePrior;
    // Closed TB: RE_change = NI - dividends → dividends = NI - RE_change
    // Unclosed TB: RE_change = -dividends → dividends = -RE_change
    dividends = isClosed ? (netIncome - reChange) : -reChange;
    // Round to avoid floating-point noise in display
    if (Math.abs(dividends) < 0.005) dividends = 0;
  }

  // Beginning balance for current period = prior period ending equity (including NI)
  const beginBal: number = pTotalEquityWithNI;
  // Beginning balance for prior period = 0 (unknown)
  const pBeginBal: number = 0;

  // Determine title based on entity type
  const entityEl = document.getElementById('nq-entity-type') as HTMLSelectElement | null;
  const entityType: string = entityEl ? entityEl.value : '';
  const eqTitle: string =
    entityType &&
    (entityType.indexOf('LLC') >= 0 ||
      entityType.indexOf('Partnership') >= 0)
      ? "Statement of Changes in Members' Equity"
      : "Statement of Stockholders' Equity";

  let html: string = stmtHeader(eqTitle);
  const tableClass: string = multi ? 'stmt-table multi-period' : 'stmt-table';
  if (multi) {
    html +=
      '<table class="' +
      tableClass +
      '"><colgroup><col class="col-label"><col class="col-current"><col class="col-prior"></colgroup><tbody>';
  } else {
    html +=
      '<table class="' +
      tableClass +
      '"><colgroup><col class="col-label"><col class="col-amount"><col class="col-total"></colgroup><tbody>';
  }
  html += columnHeaders(multi);

  // Helper: compute equity changes (exclude retained earnings, show only items that changed)
  function equityChanges(
    curEqRows: LineItem[],
    priorEqRows: LineItem[]
  ): EquityChange[] {
    const changes: EquityChange[] = [];
    curEqRows.forEach((item: LineItem) => {
      if (item.label.toLowerCase().indexOf('retained') >= 0) return;
      let priorAmt = 0;
      if (priorEqRows) {
        priorEqRows.forEach((p: LineItem) => {
          if (p.label === item.label)
            priorAmt = parseFloat(String(p.amount)) || 0;
        });
      }
      const change: number =
        (parseFloat(String(item.amount)) || 0) - priorAmt;
      if (Math.abs(change) >= 0.005) {
        changes.push({ label: item.label, amount: change });
      }
    });
    // Check for items in prior but not in current (removed equity items)
    if (priorEqRows) {
      priorEqRows.forEach((p: LineItem) => {
        if (p.label.toLowerCase().indexOf('retained') >= 0) return;
        let found = false;
        curEqRows.forEach((c: LineItem) => {
          if (c.label === p.label) found = true;
        });
        if (!found) {
          const change: number = -(parseFloat(String(p.amount)) || 0);
          if (Math.abs(change) >= 0.005)
            changes.push({ label: p.label, amount: change });
        }
      });
    }
    return changes;
  }

  if (multi) {
    // -- Prior Period Rollforward --
    html += sectionHeader('Prior Period', multi);
    html += tr({ label: 'Beginning Balance', indent: false, currentAmt: pBeginBal, priorAmt: null, isTotal: true, cls: '', multi: false });
    html += tr({ label: 'Net Income', indent: true, currentAmt: pNetIncome, priorAmt: null, isTotal: false, cls: '', multi: false });

    // Prior period equity changes (vs beginning balance of 0)
    const priorEqItems: LineItem[] = rows(state.priorData, 'equity');
    const pChanges: EquityChange[] = equityChanges(priorEqItems, []);
    pChanges.forEach((item: EquityChange) => {
      html += tr({ label: item.label, indent: true, currentAmt: item.amount, priorAmt: null, isTotal: false, cls: '', multi: false });
    });
    html += tr({ label: 'Ending Balance (Prior Period)', indent: false, currentAmt: pTotalEquityWithNI, priorAmt: null, isTotal: true, cls: '', multi: false });

    // Blank separator
    html += '<tr><td colspan="3" style="height:12px"></td></tr>';

    // -- Current Period Rollforward --
    html += sectionHeader('Current Period', multi);
    html += tr({ label: 'Beginning Balance', indent: false, currentAmt: pTotalEquityWithNI, priorAmt: null, isTotal: true, cls: '', multi: false });
    html += tr({ label: 'Net Income', indent: true, currentAmt: netIncome, priorAmt: null, isTotal: false, cls: '', multi: false });

    const curEqItems: LineItem[] = rows(state.currentData, 'equity');
    const priorEqForCompare: LineItem[] = rows(state.priorData, 'equity');
    const cChanges: EquityChange[] = equityChanges(
      curEqItems,
      priorEqForCompare
    );
    cChanges.forEach((item: EquityChange) => {
      html += tr({ label: item.label, indent: true, currentAmt: item.amount, priorAmt: null, isTotal: false, cls: '', multi: false });
    });
    // Dividends/Distributions deduction (detected from RE change)
    if (Math.abs(dividends) > 0.005) {
      const label = dividends > 0 ? 'Dividends/Distributions Paid' : 'Capital Contribution to RE';
      html += tr({ label, indent: true, currentAmt: -Math.abs(dividends) * (dividends > 0 ? 1 : -1), priorAmt: null, isTotal: false, cls: '', multi: false });
    }
    html += tr({ label: 'Ending Balance (Current Period)', indent: false, currentAmt: totalEquity, priorAmt: null, isTotal: true, cls: '', multi: false });
  } else {
    // -- Single Period Rollforward --
    html += tr({ label: 'Beginning Balance', indent: false, currentAmt: beginBal, priorAmt: null, isTotal: true, cls: '', multi });
    html += tr({ label: 'Net Income', indent: true, currentAmt: netIncome, priorAmt: null, isTotal: false, cls: '', multi });

    const eqItems: LineItem[] = rows(state.currentData, 'equity');
    const sChanges: EquityChange[] = equityChanges(eqItems, []);
    sChanges.forEach((item: EquityChange) => {
      html += tr({ label: item.label, indent: true, currentAmt: item.amount, priorAmt: null, isTotal: false, cls: '', multi });
    });
    html += tr({ label: 'Ending Balance', indent: false, currentAmt: totalEquity, priorAmt: null, isTotal: true, cls: '', multi });
  }

  html += '</tbody></table>';
  const equityEl = el('equity-statement');
  if (equityEl) equityEl.innerHTML = html;
  } finally { restoreStatementData(); }
}
